import { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, Typography } from '@mui/material';

import { getDevicesStats } from '../../../../services/stats.services';
import { DeviceStats } from '../../../../types/DeviceStats';
import useDateTimeFormat from '../../../../hooks/useDateTimeFormat';

// Common
import SkeletonRow from '../../../common/Skeleton/SkeletonRow';

export const DevicesStats = () => {
  const { toStandardDate, withTimeMode } = useDateTimeFormat();
  const [data, setData] = useState<DeviceStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateTimeRefreshed, setDateTimeRefreshed] = useState<string | undefined>();
  const [dateTimeForNextRefresh, setDateTimeForNextRefresh] = useState<string | undefined>();

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);

      try {
        const res: any = await getDevicesStats();

        if (res?.status === 200 && isMounted) {
          setData(res.data ?? []);
          setDateTimeRefreshed(res.dateTimeRefreshed);
          setDateTimeForNextRefresh(res.dateTimeForNextRefresh);
          setLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          setLoading(false);
          setData([]);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Paper sx={{ px: 2, mt: 2, border: '2px solid #e2e8f0' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px' }}>
        <h4 style={{ margin: 0 }}>Device Statistics</h4>
        <Box sx={{ display: 'flex', gap: 3 }}>
          {dateTimeRefreshed && (
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              <strong>{withTimeMode('Last Refreshed')}:</strong> {toStandardDate(dateTimeRefreshed)}
            </Typography>
          )}
          {dateTimeForNextRefresh && (
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              <strong>{withTimeMode('Next Refresh')}:</strong> {toStandardDate(dateTimeForNextRefresh)}
            </Typography>
          )}
        </Box>
      </Box>

      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Device Type</strong>
              </TableCell>
              <TableCell>
                <strong>Total Devices</strong>
              </TableCell>
              <TableCell>
                <strong>Paired Devices</strong>
              </TableCell>
              <TableCell>
                <strong>Paired %</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <SkeletonRow rows={5} columns={4} />
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align='center'>
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow key={row.deviceTypeName}>
                  <TableCell>{row.deviceTypeName}</TableCell>
                  <TableCell align='right'>{row.totalDevices}</TableCell>
                  <TableCell align='right'>{row.pairedDevices}</TableCell>
                  <TableCell align='right'>{row.pairedPercentage.toFixed(2)}%</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
